import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Container, SectionTitle } from './shared/SharedComponents';
import { WorldMap } from 'react-svg-worldmap';

// Global Stats Section
const StatsSection = styled.section`
  padding: 20px 0 100px;
  position: relative;
  overflow: visible;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: translateY(${props => props.isVisible ? '0' : '40px'});
  transition: opacity 0.8s ease, transform 0.8s ease;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  width: 100%;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 32px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: translateY(${props => props.isVisible ? '0' : '30px'}) scale(${props => props.isVisible ? '1' : '0.95'});
  transition-delay: 0.1s;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  }
`;

const StatNumber = styled.div`
  font-size: clamp(32px, 4.5vw, 36px);
  font-weight: 900;
  color: #1d334e;
  margin-bottom: 12px;
  line-height: 1;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 4px;
`;

const StatDetail = styled.div`
  font-size: 14px;
  color: rgba(29, 51, 78, 0.8);
  font-weight: 500;
  margin-bottom: 8px;
`;

const MapContainer = styled.div`
  margin-top: 30px;
  border-radius: 24px;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: translateY(${props => props.isVisible ? '0' : '30px'});
  transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.1s;
`;

const MapWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  overflow: hidden;
  
  figure {
    margin: 0 !important;
    display: block !important;
  }
  
  figure, svg {
    flex-shrink: 0;
  }
`;

const GlobalStats = ({ data, isVisible }) => {
  const [mapSize, setMapSize] = useState(600);

  // Calculate map size based on window width
  useEffect(() => {
    const calculateMapSize = () => {
      const windowWidth = window.innerWidth;
      // Container has max-width: 1200px
      const containerMaxWidth = 1000;
      const containerPadding = 40; // Container side padding

      // Calculate effective width (no MapContainer padding now)
      const availableWidth = Math.min(windowWidth - containerPadding, containerMaxWidth);

      // Use 75% of available width for a smaller map
      const effectiveWidth = Math.floor(availableWidth * 0.75);

      // Set map size with minimum of 240px
      setMapSize(Math.max(240, effectiveWidth));
    };

    calculateMapSize();
    window.addEventListener('resize', calculateMapSize);
    return () => window.removeEventListener('resize', calculateMapSize);
  }, []);

  // Countries data for world map highlighting
  const highlightedCountries = [
    { country: 'dz', value: 1 }, // Algeria
    { country: 'ar', value: 1 }, // Argentina
    { country: 'au', value: 1 }, // Australia
    { country: 'at', value: 1 }, // Austria
    { country: 'az', value: 1 }, // Azerbaijan
    { country: 'bh', value: 1 }, // Bahrain
    { country: 'by', value: 1 }, // Belarus
    { country: 'be', value: 1 }, // Belgium
    { country: 'br', value: 1 }, // Brazil
    { country: 'bn', value: 1 }, // Brunei
    { country: 'ca', value: 1 }, // Canada
    { country: 'cl', value: 1 }, // Chile
    { country: 'bg', value: 1 }, // Bulgaria
    { country: 'cn', value: 1 }, // China
    { country: 'tw', value: 1 }, // Taiwan
    { country: 'cz', value: 1 }, // Czech Republic
    { country: 'dk', value: 1 }, // Denmark
    { country: 'ee', value: 1 }, // Estonia
    { country: 'eg', value: 1 }, // Egypt
    { country: 'fi', value: 1 }, // Finland
    { country: 'fr', value: 1 }, // France
    { country: 'de', value: 1 }, // Germany
    { country: 'gr', value: 1 }, // Greece
    { country: 'gn', value: 1 }, // Guinea
    { country: 'hu', value: 1 }, // Hungary
    { country: 'hk', value: 1 }, // Hong Kong
    { country: 'in', value: 1 }, // India
    { country: 'id', value: 1 }, // Indonesia
    { country: 'it', value: 1 }, // Italy
    { country: 'jp', value: 1 }, // Japan
    { country: 'kz', value: 1 }, // Kazakhstan
    { country: 'kw', value: 1 }, // Kuwait
    { country: 'kg', value: 1 }, // Kyrgyzstan
    { country: 'lb', value: 1 }, // Lebanon
    { country: 'lv', value: 1 }, // Latvia
    { country: 'lt', value: 1 }, // Lithuania
    { country: 'my', value: 1 }, // Malaysia
    { country: 'mc', value: 1 }, // Monaco
    { country: 'mn', value: 1 }, // Mongolia
    { country: 'nl', value: 1 }, // Netherlands
    { country: 'no', value: 1 }, // Norway
    { country: 'om', value: 1 }, // Oman
    { country: 'ph', value: 1 }, // Philippines
    { country: 'pt', value: 1 }, // Portugal
    { country: 'pl', value: 1 }, // Poland
    { country: 'qa', value: 1 }, // Qatar
    { country: 'ru', value: 1 }, // Russia
    { country: 'sa', value: 1 }, // Saudi Arabia
    { country: 'rs', value: 1 }, // Serbia
    { country: 'sg', value: 1 }, // Singapore
    { country: 'sk', value: 1 }, // Slovakia
    { country: 'si', value: 1 }, // Slovenia
    { country: 'kr', value: 1 }, // South Korea
    { country: 'za', value: 1 }, // South Africa
    { country: 'es', value: 1 }, // Spain
    { country: 'se', value: 1 }, // Sweden
    { country: 'ch', value: 1 }, // Switzerland
    { country: 'tj', value: 1 }, // Tajikistan
    { country: 'th', value: 1 }, // Thailand
    { country: 'tr', value: 1 }, // Turkey
    { country: 'tm', value: 1 }, // Turkmenistan
    { country: 'ae', value: 1 }, // United Arab Emirates
    { country: 'gb', value: 1 }, // United Kingdom
    { country: 'us', value: 1 }, // United States of America
    { country: 'ug', value: 1 }, // Uganda
    { country: 'uz', value: 1 }, // Uzbekistan
    { country: 'vn', value: 1 }, // Vietnam
  ];

  if (!data) return null;

  return (
    <StatsSection data-animate isVisible={isVisible}>
      <Container>
        <SectionTitle className="with-underline">{data.title}</SectionTitle>

        <StatsContainer>
          {data.stats.map((stat, index) => (
            <StatCard key={index} isVisible={isVisible}>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
              {stat.detail && <StatDetail>{stat.detail}</StatDetail>}
            </StatCard>
          ))}
        </StatsContainer>

        <MapContainer isVisible={isVisible}>
          <MapWrapper>
            <div style={{ width: mapSize, flexShrink: 0 }}>
              <WorldMap
                color="#1d334e"
                size={mapSize}
                data={highlightedCountries}
                tooltipBgColor="transparent"
                tooltipTextColor="transparent"
                backgroundColor="transparent"
                borderColor="transparent"
                richInteraction={false}
                styleFunction={(context) => {
                  const { countryValue } = context;
                  return {
                    fill: countryValue
                      ? '#1d334e'
                      : 'rgba(230, 235, 240, 0.5)',
                    fillOpacity: countryValue ? 0.85 : 1,
                    stroke: countryValue
                      ? 'rgba(15, 27, 41, 0.4)'
                      : 'rgba(200, 210, 220, 0.4)',
                    strokeWidth: 0.5,
                    strokeOpacity: 1,
                    cursor: 'default',
                    textAlign: 'center',
                  };
                }}
              />
            </div>
          </MapWrapper>
        </MapContainer>
      </Container>
    </StatsSection>
  );
};

export default GlobalStats;

